<template>
  <app-dialog
    :title="title"
    v-bind="$attrs"
    v-model="open"
    :closable="!reset"
  >
    <template v-if="reset && step != 2">
      <div v-if="step == 0">
        Let's set up a custom pin so we can protect your information.
      </div>
      <div v-if="step == 1">
        Please confirm your new pin.
      </div>
    </template>
    <div v-else-if="step != 2">
      <template v-if="user.has_pin">
        Enter your new PIN.
      </template>
      <template v-else-if="user.id == $auth.user!.id">
        Since there are multiple users in this Idly account, you can set a PIN to protect your profile.
      </template>
      <template v-else>
        Since there are multiple users in this Idly account, you can set a PIN to protect {{ user.name }}'s profile.
      </template>
    </div>
    <div v-else>
      <template v-if="user.has_pin && user.id == $auth.user!.id">
        Your new PIN is set. You will need to enter it every time you open your profile in Idly.
      </template>
      <template v-else-if="user.id == $auth.user!.id">
        Your PIN is set. You will need to enter it every time you open your profile in Idly.
      </template>
      <template v-else>
        {{ user.name }}’s PIN is set. They will need to enter it every time they open their profile in Idly.
      </template>
    </div>

    <div
      class="text-center mb-n4"
      v-if="!hideOptOut && !$auth.roleAtLeast(roles.AUTONOMOUS) && step != 2"
    >
      <v-checkbox
        v-model="noPinRequired"
        class="d-inline-block"
        hide-details
        label="Opt out of using a pin to protect your profile."
      />
    </div>

    <template v-if="!noPinRequired">
      <div
        class="text-red text-center mt-4"
        v-if="error"
        v-text="error"
      />

      <v-otp-input
        v-if="step === 0"
        v-model="pin"
        :error="!!error"
        length="4"
        width="250"
        class="pb-0"
        base-color="primary"
        @keydown.enter="next"
        @change="error = ''"
      />
      <v-otp-input
        v-else-if="step === 1"
        v-model="confirmPin"
        :error="!!error"
        length="4"
        width="250"
        class="pb-0"
        base-color="primary"
        @keydown.enter="next"
        @change="error = ''"
      />

      <div class="d-flex ga-4 mt-4 justify-center">
        <v-btn-secondary
          v-if="showBack"
          class="flex-grow-1"
          @click="prev"
          :disabled="loading === 'continue'"
        >
          EDIT PIN
        </v-btn-secondary>
        <v-btn-primary
          :class="{'w-50': !showBack, 'flex-grow-1': showBack}"
          @click="next"
          :disabled="step === 0 && pin.length !== 4 || step === 1 && confirmPin.length !== 4"
          :loading="loading === 'continue'"
        >
          {{ step === 0 ? 'CONTINUE' : step === 1 ? 'SET PIN' : 'DONE' }}
        </v-btn-primary>
      </div>
    </template>
    <div
      v-else
      class="d-flex ga-4 mt-4 justify-center"
    >
      <v-btn-primary
        class="w-50"
        @click="savePin"
        :loading="loading === 'continue'"
      >
        Continue
      </v-btn-primary>
    </div>
  </app-dialog>
</template>
<script lang="ts">
import AppDialog from "@/components/AppDialog.vue"
import type { PropType } from 'vue'
import {roles} from "@/enums/roles"

export default {
  name: 'PinDialog',
  components: {AppDialog},
  inheritAttrs: false,
  emits: ['update:modelValue', 'success', 'add-member', 'close'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object as PropType<App.Models.User>,
      required: true
    },
    reset: Boolean,
    hideOptOut: Boolean,
  },
  data() {
    return {
      step: 0,
      pin: '',
      confirmPin: '',
      error: '',
      loading: '',
      noPinRequired: false,
      roles,
    }
  },
  computed: {
    open: {
      get() {
        return this.modelValue
      },
      set(value: boolean) {
        this.$emit('update:modelValue', value)
        if (!value) {
          this.$emit('close')
        }
      }
    },
    showBack() {
      return this.step > 0 && this.step < 2
    },
    title() {
      if (this.reset) {
        return this.step === 0 ? 'Reset Your Pin' : this.step === 1 ? 'Confirm Your Pin Reset' : 'Pin Reset'
      }
      const action = this.user.has_pin ? 'Change' : 'Add'
      return this.step === 0 ? `${action} PIN` : this.step === 1 ? 'Confirm PIN' : 'PIN Updated'
    }
  },
  methods: {
    async next() {
      if (this.step === 0 && this.pin.length !== 4 || this.step === 1 && this.confirmPin.length !== 4) {
        this.error = 'Pin must be 4 digits.'
        return
      }

      if (this.step === 1) {
        if (this.pin !== this.confirmPin) {
          this.error = 'Uh-oh. Pins don\'t match. Please try again.'
          return
        }
        const result = await this.savePin()
        if (!result) return
      }

      if (this.step === 2) {
        if (this.user.id === this.$auth.user!.id) {
          this.$emit('add-member')
        }

        this.$emit('success')
        this.open = false
        return
      }
      this.step++
    },
    prev() {
      if (this.step === 0) return
      this.step--
    },
    async savePin() {
      this.error = ''
      this.loading = 'continue'
      const { data: { message, errors }, status } = await this.$http.post('/users/' + this.user.id + '/set-pin', { pin: this.pin, noPin: this.noPinRequired }).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return false
      }

      if (this.user.id === this.$auth.user!.id) {
        this.$auth.loadUser(true)
      }

      if (this.noPinRequired) {
        this.$emit('success')
        this.open = false
        return true
      }

      return true
    }
  }
}
</script>
